import { useState, useRef, useEffect } from 'react';
import { UseFormRegisterReturn, FieldValues, Path } from 'react-hook-form';

import { DropdownProps } from './types';

import IconChevron from '@/assets/icons/iconChevron.svg';
import { classNames } from '@/shared/lib/classNames/classNames';

export const Dropdown = <T extends FieldValues>({
  label,
  options,
  error,
  watch,
  onChange,
  className,
  ...inputProps
}: DropdownProps<T> & Omit<UseFormRegisterReturn, 'onChange'>) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedValue = watch(inputProps.name as Path<T>);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleDocumentClick = (event: Event) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('click', handleDocumentClick);
    }

    return () => document.removeEventListener('click', handleDocumentClick);
  }, [isOpen]);

  return (
    <div
      ref={dropdownRef}
      className={classNames('relative w-[400px]', {}, [className])}
    >
      <div className="relative h-14">
        <div
          onClick={() => setIsOpen((isOpen) => !isOpen)}
          {...inputProps}
          id={label}
          className={classNames(
            'peer flex h-full w-full cursor-pointer rounded-xl bg-customSurfaceContrastLow px-3 py-2 pt-6 text-base focus:outline-none',
            { 'border border-statusError': !!error },
          )}
        >
          {options.find(({ value }) => value === selectedValue)?.label}
          <IconChevron
            className={classNames(
              'absolute bottom-[1.125rem] right-[1.125rem] top-[1.125rem] transition-transform',
              { 'rotate-180': isOpen },
            )}
          />
        </div>
        <label
          htmlFor={label}
          className={classNames(
            'pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 cursor-text text-base tracking-[-0.01rem] text-textBaseSecondary transition-all duration-300 peer-focus:top-4',
            {
              'top-4 text-sm leading-[18px]': selectedValue,
              'peer-hover:text-textBasePrimary': !selectedValue,
            },
          )}
        >
          {label}
        </label>
      </div>
      {!!error?.message && (
        <span className="block h-5 text-sm leading-[1.125rem] text-statusError">
          {error?.message}
        </span>
      )}
      {isOpen && (
        <div className="absolute top-16 z-20 w-[400px] rounded-xl bg-surfaceBaseInversePrimary2 max-sm:w-full">
          <ul className="w-full rounded-xl bg-customSurfaceContrastLow py-2">
            {options.map(({ value, label }) => (
              <li
                onClick={() => {
                  onChange(value);
                  setIsOpen(false);
                }}
                className="flex h-[32px] cursor-pointer items-center px-3 text-[16px] text-surfaceBasePrimary hover:bg-surfaceBaseInverseSecondary"
                key={value}
              >
                {label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
